/*
  Create a new Ministry Checkup survey. Admin only. Admin selects date and users to invite.
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';

const MinistryCheckupNewPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.role === 'admin';

  useEffect(() => {
    if (user && !isAdmin) {
      navigate('/ministry-checkup', { replace: true });
    }
  }, [user, isAdmin, navigate]);
  const [title, setTitle] = useState('Ministry Checkup');
  const [surveyDate, setSurveyDate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState('');
  const [usersError, setUsersError] = useState('');
  const [showUnselectedOnly, setShowUnselectedOnly] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchUsers = async () => {
      setUsersLoading(true);
      setUsersError('');
      const { ok, data } = await get('/api/surveys/users');
      setUsersLoading(false);
      if (ok) {
        setUsers(Array.isArray(data) ? data : []);
      } else {
        setUsersError(data?.message || 'Failed to load users. You can still create a survey and add users later.');
      }
    };
    fetchUsers();
  }, [isAdmin]);

  const toggleUser = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const displayUsers = showUnselectedOnly ? users.filter((u) => !selectedIds.has(u.id)) : users;
  const unselectedCount = users.filter((u) => !selectedIds.has(u.id)).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!surveyDate) {
      setError('Please select a date.');
      return;
    }
    setLoading(true);
    const { ok, status, data } = await post('/api/surveys', {
      title: title.trim() || 'Ministry Checkup',
      surveyDate,
      invitedUserIds: Array.from(selectedIds),
    });
    setLoading(false);
    if (ok) {
      navigate(`/ministry-checkup/${data._id}/completion`);
    } else {
      setError(getErrorMessage({ status, data }, 'Failed to create survey'));
    }
  };

  if (!user || !isAdmin) {
    return <p style={{ padding: '1rem', textAlign: 'center' }}>Loading…</p>;
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title="Create Ministry Checkup" backTo="/ministry-checkup" backLabel="← Ministry Checkup" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
        Pick a date and select who to invite. Invitees receive an email and can take the survey anytime.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Survey title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ministry Checkup"
          />
        </div>

        <div className="form-group">
          <label>Survey date</label>
          <div style={{ position: 'relative', width: '100%', marginTop: '0.25rem' }}>
            <div
              style={{
                padding: '0.35rem 0.5rem',
                border: '1px solid var(--cr-border)',
                borderRadius: '8px',
                backgroundColor: 'var(--cr-surface)',
                color: surveyDate ? 'var(--cr-text)' : 'var(--cr-text-muted)',
                fontSize: '1rem',
              }}
            >
              {surveyDate ? new Date(surveyDate + 'T12:00:00').toLocaleDateString('en-US') : 'Tap to select date'}
            </div>
            <input
              type="date"
              value={surveyDate}
              onChange={(e) => setSurveyDate(e.target.value)}
              required
              aria-label="Survey date"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                margin: 0,
                zIndex: 1,
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Select users to invite</label>
          {usersLoading && <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>Loading users…</p>}
          {usersError && <p style={{ color: 'var(--cr-error)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>{usersError}</p>}
          {!usersLoading && users.length === 0 && !usersError && (
            <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              No users found. Users must sign up first to appear here.
            </p>
          )}
          {users.length > 0 && (
            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowUnselectedOnly((v) => !v)}
              disabled={!showUnselectedOnly && unselectedCount === 0}
              style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}
            >
              {showUnselectedOnly ? 'Show all users' : (unselectedCount > 0 ? `Show ${unselectedCount} not yet selected` : 'All users selected')}
            </button>
          )}
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid var(--cr-border)', borderRadius: '8px', padding: '0.5rem' }}>
            {displayUsers.map((u) => (
              <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(u.id)}
                  onChange={() => toggleUser(u.id)}
                />
                <span>
                  {u.firstName} {u.lastName}
                  {u.email && <span style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}> ({u.email})</span>}
                </span>
              </label>
            ))}
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--cr-text-muted)', marginTop: '0.25rem' }}>
            {selectedIds.size} selected
          </p>
        </div>

        {error && <p style={{ color: 'var(--cr-error)', marginBottom: '0.5rem' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Creating…' : 'Create Survey'}
          </button>
          <button type="button" className="btn-outline" onClick={() => navigate('/ministry-checkup')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default MinistryCheckupNewPage;
